import cx from "classnames";
import React from "react";

import * as styles from "./FormattedText.module.css";

const boldStart = "*>";
const boldEnd = "<";
const breakline = "<br/>";
const linkStart = "<link";
const linkEnd = "/>";
const italicStart = "[italic]";
const italicEnd = "[/italic]";
const underlinedItalicStart = "[underlinedItalic]";
const underlinedItalicEnd = "[/underlinedItalic]";

type FormattedTextProps = {
  text: string;
  className?: string;
};

type LinkInfo = {
  text: string;
  url: string;
  openInNewTab?: boolean;
};

export const FormattedText = (props: FormattedTextProps) => {
  let textWithBreaklines: string[] = [];

  const renderItalic = (content: React.ReactNode): React.ReactNode => {
    if (typeof content !== "string" || !content.includes(italicStart)) {
      return content;
    }

    const parts: React.ReactNode[] = [];
    let remainingContent = content;
    let partIndex = 0;

    while (remainingContent.includes(italicStart)) {
      const startIndex = remainingContent.indexOf(italicStart);
      const endIndex = remainingContent.indexOf(italicEnd, startIndex + italicStart.length);

      if (endIndex === -1) {
        parts.push(remainingContent);
        return parts;
      }

      parts.push(remainingContent.slice(0, startIndex));
      parts.push(
        <em key={`italic-${partIndex}`}>
          {remainingContent.slice(startIndex + italicStart.length, endIndex)}
        </em>
      );
      remainingContent = remainingContent.slice(endIndex + italicEnd.length);
      partIndex++;
    }

    parts.push(remainingContent);
    return parts;
  };

  const renderUnderlinedItalic = (content: React.ReactNode): React.ReactNode => {
    if (typeof content !== "string" || !content.includes(underlinedItalicStart)) {
      return content;
    }

    const parts: React.ReactNode[] = [];
    let remainingContent = content;
    let partIndex = 0;

    while (remainingContent.includes(underlinedItalicStart)) {
      const startIndex = remainingContent.indexOf(underlinedItalicStart);
      const endIndex = remainingContent.indexOf(
        underlinedItalicEnd,
        startIndex + underlinedItalicStart.length
      );

      if (endIndex === -1) {
        parts.push(remainingContent);
        return parts;
      }

      parts.push(remainingContent.slice(0, startIndex));
      parts.push(
        <u key={`underlined-italic-${partIndex}`}>
          <em>{remainingContent.slice(startIndex + underlinedItalicStart.length, endIndex)}</em>
        </u>
      );
      remainingContent = remainingContent.slice(endIndex + underlinedItalicEnd.length);
      partIndex++;
    }

    parts.push(remainingContent);
    return parts;
  };

  const parseLink = (linkString: string): LinkInfo | null => {
    const textMatch = linkString.match(/text=['"]([^'"]*)['"]/);
    const urlMatch = linkString.match(/url=([^\s'">]+)/);
    const targetMatch = linkString.match(/target=['"]_blank['"]/);

    if (textMatch && urlMatch) {
      return {
        text: textMatch[1],
        url: urlMatch[1],
        openInNewTab: !!targetMatch,
      };
    }
    return null;
  };

  const traverseAndParseBreakLines = () => {
    // Split the description into an array by searching the breakline sintax.
    // Each position is a phrase.
    if (props.text) {
      textWithBreaklines = props.text.split(breakline);
    }
  };

  const processLineWithLinksAndBold = (line: string) => {
    // First, extract links and replace them with placeholders
    const linkRegex = new RegExp(`${linkStart}[^>]*${linkEnd}`, "g");
    const links = line.match(linkRegex) || [];
    const linkPlaceholders: string[] = [];
    let processedLine = line;

    // Replace links with placeholders
    links.forEach((link, index) => {
      const placeholder = `__LINK_${index}__`;
      linkPlaceholders.push(placeholder);
      processedLine = processedLine.replace(link, placeholder);
    });

    // Now process bold text on the line without links
    const boldProcessedParts = processedLine.split(boldStart);
    const boldPositions = new Set<number>();

    for (let i = 0; i < boldProcessedParts.length; i++) {
      const part = boldProcessedParts[i];
      if (part.includes(boldEnd)) {
        boldPositions.add(i);
        boldProcessedParts[i] = part.slice(0, -1); // Remove the bold end marker
      }
    }

    // Render the parts
    return boldProcessedParts.map((part, index) => {
      let content: React.ReactNode = part;

      // Replace link placeholders with actual links
      linkPlaceholders.forEach((placeholder, linkIndex) => {
        if (typeof content === "string" && content.includes(placeholder)) {
          const linkInfo = parseLink(links[linkIndex]);
          if (linkInfo) {
            const beforeLink = content.split(placeholder)[0];
            const afterLink = content.split(placeholder)[1];

            content = (
              <React.Fragment key={`link-${linkIndex}`}>
                {beforeLink}
                <a
                  href={linkInfo.url}
                  target={linkInfo.openInNewTab ? "_blank" : undefined}
                  rel={linkInfo.openInNewTab ? "noopener noreferrer" : undefined}
                >
                  {linkInfo.text}
                </a>
                {afterLink}
              </React.Fragment>
            );
          }
        }
      });

      const formattedContent = renderItalic(renderUnderlinedItalic(content));
      const element = boldPositions.has(index) ? <b>{formattedContent}</b> : formattedContent;
      return <React.Fragment key={`part-${index}`}>{element}</React.Fragment>;
    });
  };

  traverseAndParseBreakLines();

  return (
    <>
      {textWithBreaklines.map((line, lineIndex) => (
        <span key={`line-${lineIndex}`} className={cx(styles.text, props.className)}>
          {processLineWithLinksAndBold(line)}
          {lineIndex < textWithBreaklines.length - 1 && <br />}
        </span>
      ))}
    </>
  );
};
